import { Injectable, NgZone } from '@angular/core';
import { ThemeE } from '@enums/theme.enum';
import { BehaviorSubject, Observable } from 'rxjs';

import { enable, disable, setFetchMethod, isEnabled } from 'darkreader';

/**
 * O dark mode roda o Dark Reader em modo dynamic, que observa mutacoes do DOM e
 * reescreve inline styles. Dentro do editor rich text (ProseMirror/TipTap) isso
 * cria um loop: o Dark Reader altera o style inline -> o ProseMirror detecta que
 * o DOM divergiu do doc e re-renderiza -> nova mutacao -> Dark Reader de novo.
 * Conteudo de etapa com inline style + mention trava a aba com 100% de CPU.
 * Ignorar o inline style dentro do editor quebra o ciclo.
 */
const DARK_READER_FIXES = {
  invert: [],
  css: '',
  ignoreInlineStyle: ['.ProseMirror', '.ProseMirror *'],
  ignoreImageAnalysis: [],
  disableStyleSheetsProxy: false,
};

@Injectable({
  providedIn: 'root',
})
export class ColorThemeService {
  private selectedTheme: BehaviorSubject<ThemeE> = new BehaviorSubject<ThemeE>(ThemeE.LIGHT);
  private themeObservable: Observable<ThemeE> = this.selectedTheme.asObservable();

  constructor(private ngZone: NgZone) {
    setFetchMethod(window.fetch);
    this.init();
  }

  public setTheme(theme: ThemeE) {
    this.ngZone.run(() => {
      this.selectedTheme.next(theme);
    });
  }

  public getThemeObservable(): Observable<ThemeE> {
    return this.themeObservable;
  }

  public isDarkThemeEnabled() {
    return isEnabled();
  }

  private init() {
    this.themeObservable.subscribe((theme) => {
      this.applyTheme(theme);
    });

    this.setTheme(this.selectedTheme.value);
  }

  private applyTheme(theme: ThemeE) {
    this.ngZone.runOutsideAngular(() => {
      return {
        [ThemeE.LIGHT]: () => disable(),
        [ThemeE.DARK]: () => enable({}, DARK_READER_FIXES),
        [ThemeE.SYSTEM]: () => this.systemTheme(),
      }[theme]();
    });
  }

  private systemTheme() {
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.ngZone.runOutsideAngular(() => {
      if (prefersDark) {
        enable({}, DARK_READER_FIXES);
      } else {
        disable();
      }
    });
  }
}
